import React from 'react';
import { Box } from '@mui/material';
import { loadFromLocalStorage } from '../../Common/Validation/LocalStorage';
import { TableColumn } from 'react-data-table-component';
import CustomMaterialMenu from '../../Component/DataGrid/Plugins/CustomMaterialMenu/CustomMaterialMenu';
import DataGrid from '../../Component/DataGrid/DataGrid';
import ReferralService from '../../Services/ReferralService';
import { SyncContext, SyncContextType } from '../../Contexts/BatchSyncContext';
import { IReferral, ReferralsProps, ReferralsState } from './Interface';
import moment from 'moment';
import RefereesDialog from './RefereesDialog';
import UserAndPermissionService from '../../Services/UserAndPermissionService';
import ReferralSettings from './ReferralSettings';
import { ShowAlert } from '../../Component/ShowAlert';
import InvitedList from './InviteList';

class Referrals extends React.Component<ReferralsProps, ReferralsState> {
    static contextType = SyncContext;
    context!: SyncContextType;

    private tableColumns: TableColumn<IReferral>[] = [
        {
            name: 'Referrer Name',
            selector: row => row.name || '-',
            id: 'name',
            sortable: true,
            minWidth: '150px',
            wrap: true,
        },
        {
            name: 'Email',
            selector: row => row.email || '-',
            id: 'email',
            sortable: true,
            minWidth: '200px',
            wrap: true,
        },
        {
            name: 'Country',
            selector: row => row.countryName || '-',
            id: 'countryName',
            sortable: true,
            center: true,
            minWidth: '120px'
        },
        {
            name: 'Referral Code',
            selector: row => row.referralCode ? row.referralCode : '-',
            id: 'referralCode',
            sortable: true,
            minWidth: '180px',
            wrap: true,
            center: true,
        },
        {
            name: 'Successful Referrals',
            selector: row => row.successfullReferrals?.toString() || '0',
            id: 'successfullReferrals',
            sortable: true,
            center: true,
            minWidth: '130px'
        },
        {
            name: 'Total Bonus in Local Currency',
            selector: row => `${row.localCurrencySymbol ?? ""} ${row.totalLocalBonus ?? 0}`,
            id: 'totalLocalBonus',
            sortable: true,
            right: true,
            minWidth: '133px'
        },
        {
            name: 'Total Bonus in USD',
            selector: row => `${row.convertedCurrencySymbolUSD} ${row.totalConvertedBonusUSD ?? 0}`,
            id: 'totalConvertedBonusUSD',
            sortable: true,
            right: true,
            minWidth: '120px'
        },
        {
            name: 'Last Referred Date',
            id: 'lastReferredDate',
            selector: row => row.lastReferredDate
                ? moment(row.lastReferredDate).format('MM-DD-YYYY')
                : '-',
            sortable: true,
            minWidth: '130px'
        },
        {
            name: 'Actions',
            cell: row => {
                const { referrals } = this.state;

                // If view action is not allowed, render nothing
                if (!referrals.isViewRefereesVisible) return null;

                const menuItems = [
                    { key: "viewreferee", label: "View Referees" },
                    { key: "view_invites", label: "View Invites" }
                ];

                return (
                    <CustomMaterialMenu
                        fontSize="small"
                        multiPopoverItems={menuItems}
                        onPopoverClick={(key_: string) => {
                            if (key_ === "viewreferee") {
                                this.setState({
                                    isOpenRefreesDialog: true,
                                    selectedReferrer: row
                                });
                            }
                            if (key_ === "view_invites") {
                                this.setState({
                                    isOpenIvitedDialog: true,
                                    selectedReferrer: row
                                });
                            }
                        }}
                        disabled={false}
                    />
                );
            },
            allowOverflow: true,
            button: true,
            width: '120px'
        }
    ];


    constructor(props_: ReferralsProps) {
        super(props_);
        this.state = {
            accessToken: '',
            referralData: [],
            currentPage: 1,
            currentRowsPerPage: 10,
            totalRows: 0,
            searchText: '',
            selectedFilter: '',
            filterValue: '',
            isOpenReferralSettings: false,
            isOpenRefreesDialog: false,
            selectedReferrer: {} as IReferral,
            referrals: {
                isVisible: true,
                isDownloadVisible: true,
                isReferralSettingsVisible: true,
                isSaveSettingsAllowed: true,
                isViewRefereesVisible: true,
            },
            isOpenIvitedDialog: false
        };
    }

    async componentDidMount() {
        try {
            const accessToken_ = await loadFromLocalStorage('accessToken');
            const profile_ = await loadFromLocalStorage('profile');

            if (!accessToken_ || !profile_) {
                localStorage.clear();
                window.location.href = '/';
                return;
            }

            const accessToken = accessToken_.toString();
            const userProfile = typeof profile_ === 'string' ? JSON.parse(profile_) : profile_;

            this.setState({ accessToken }, async () => {
                try {
                    if (userProfile?.roleId) {
                        const permissionResponseRaw = await UserAndPermissionService.getPermissions(accessToken, userProfile?.roleId);
                        const permissionResponse = await permissionResponseRaw.json();

                        if (!permissionResponse?.permissions?.referrals?.isVisible) {
                            console.warn("Permission denied, logging out...");
                            localStorage.clear();
                            window.location.href = '/';
                            return;
                        }

                        const permissions = permissionResponse.permissions

                        this.context.dispatch({
                            type: "SET_PERMISSION", payload: {
                                permissions
                            },
                        });

                        const referrals = permissionResponse.permissions.referrals

                        this.setState({ referrals }, () => {
                            this.getReferralData()
                        });
                    } else {
                        this.getReferralData()
                    }
                } catch (error) {
                    console.error("Error fetching permissions:", error);
                }
            });
        } catch (error) {
            console.error('Error loading referral data:', error);
        }
    }

    private getReferralData = async () => {
        const { accessToken, currentPage, currentRowsPerPage, searchText, selectedFilter, filterValue } = this.state;

        const pageIndex = currentPage - 1;

        let pagination: any = {
            pageNumber: pageIndex,
            recordPerPage: currentRowsPerPage
        };

        if (searchText) {
            pagination["searchText"] = searchText;
        }

        if (selectedFilter) {
            pagination[selectedFilter] = filterValue;
        }

        try {
            const response = await ReferralService.getReferralList(accessToken, pagination);

            if (response.ok) {
                const result = await response.json();

                const referralDetails = Array.isArray(result.referralDetails) ? result.referralDetails : [];

                // if (referralDetails.length === 0) {
                //     ShowAlert("Info", "No referral activity recorded yet.", "info").then(() => {
                //         this.setState({
                //             referralData: [],
                //             totalRows: 0,
                //         });
                //     });
                // } else {
                this.setState({
                    referralData: referralDetails,
                    totalRows: result.totalPages || 0,
                });
                // }
            }
        } catch (error) {
            console.error("Error fetching referral list:", error);
        }
    };

    render() {
        const { accessToken, isOpenReferralSettings, isOpenRefreesDialog, referralData, selectedReferrer, isOpenIvitedDialog } = this.state;
        const {
            isDownloadVisible,
            isReferralSettingsVisible,
            isSaveSettingsAllowed,
        } = this.state.referrals
        return (
            <Box className="body-sub-container table-container">
                <DataGrid
                    title="Referrals"
                    search="Referrals"
                    defaultSearch={this.state.searchText}
                    onSearch={(text) => this.setState({ searchText: text, currentPage: 1 }, this.getReferralData)}
                    onFilterChange={(selectedFilter, filterValue) =>
                        this.setState({ selectedFilter, filterValue, currentPage: 1 }, this.getReferralData)
                    }
                    columns={this.tableColumns}
                    data={referralData}
                    onChangePage={(page: number, totalRows: number) => {
                        this.setState({
                            currentPage: page
                        }, () => {
                            this.getReferralData();
                        })
                    }}
                    onChangeRowsPerPage={(currentRowsPerPage_: number, currentPage_: number) => {
                        this.setState({
                            currentRowsPerPage: currentRowsPerPage_,
                            currentPage: currentPage_
                        }, () => {
                            this.getReferralData();
                        })
                    }}
                    referralSettings={isReferralSettingsVisible}
                    isOpenReferralSettingsDialog={() => this.setState({ isOpenReferralSettings: true })}
                    pagination
                    paginationTotalRows={this.state.totalRows}
                    //exportCsv={isDownloadVisible}
                    paginationDefaultPage={1}

                />
                {isOpenReferralSettings &&
                    <ReferralSettings
                        open={isOpenReferralSettings}
                        onClose={(state: boolean, action: string) => {
                            this.setState({
                                isOpenReferralSettings: false
                            })
                        }}
                        accessToken={this.state.accessToken}
                        isSaveSettingsAllowed={isSaveSettingsAllowed}
                    />
                }
                {isOpenRefreesDialog && selectedReferrer &&
                    <RefereesDialog
                        open={isOpenRefreesDialog}
                        accessToken={accessToken}
                        refreshData={() => this.getReferralData()}
                        onClose={(state: boolean, action: string) => {
                            this.setState({
                                isOpenRefreesDialog: false
                            })
                        }}
                        selectedReferrerId={selectedReferrer.userId || ''}
                    />
                }
                {isOpenIvitedDialog && selectedReferrer &&
                    <InvitedList
                        open={isOpenIvitedDialog}
                        accessToken={accessToken}
                        refreshData={() => this.getReferralData()}
                        onClose={(state: boolean, action: string) => {
                            this.setState({
                                isOpenIvitedDialog: false
                            })
                        }}
                        selectedReferrerId={selectedReferrer.userId || ''}
                    />
                }
            </Box>
        );
    }
}

export default Referrals;
